import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { AbstractOrderApiService } from '../abstract/abstract-order-api-service';
import {
  OrderModel,
  EmployeeDTO,
  RequestTemplateDTO,
  T22EntityItemDTO,
  RequestActDTO,
  StatusDTO,
  OrdersForNotification,
} from 'src/app/store/order/model';
import { Observable } from 'rxjs';
import { AppConstants } from '../../constants/app-constants';
import { InteractorResponse } from '../../models/interactor-response';

const baseUrl = `/api/orders`;

@Injectable()
export class OrderApiService extends AbstractOrderApiService {
  private http = inject(HttpClient);
  datepipe = inject(DatePipe);

  getOrders(userId: number, fromDate: Date, toDate: Date): Observable<InteractorResponse<OrderModel[]>> {
    let fromDateStr = this.datepipe.transform(fromDate, 'yyyy-MM-dd');
    let toDateStr = this.datepipe.transform(toDate, 'yyyy-MM-dd');

    return this.http.get<InteractorResponse<OrderModel[]>>(`${baseUrl}?userId=${userId}&fromDate=${fromDateStr}&toDate=${toDateStr}`);
  }

  getOrderById(id: number, userId: number): Observable<InteractorResponse<OrderModel>> {
    return this.http.get<InteractorResponse<OrderModel>>(`${baseUrl}/${id}?userId=${userId}`);
  }

  getForWhomEmployees(userId: number, categoryId: number, serviceId: number, mask: string): Observable<InteractorResponse<EmployeeDTO[]>> {
    return this.http.get<InteractorResponse<EmployeeDTO[]>>(
      `${baseUrl}/for-whom-employees?userId=${userId}&categoryId=${categoryId}&serviceId=${serviceId}&mask=${mask}`
    );
  }

  getInitiatorEmployees(
    userId: number,
    categoryId: number,
    serviceId: number,
    mask: string
  ): Observable<InteractorResponse<EmployeeDTO[]>> {
    return this.http.get<InteractorResponse<EmployeeDTO[]>>(
      `${baseUrl}/initiator-employees?userId=${userId}&categoryId=${categoryId}&serviceId=${serviceId}&mask=${mask}`
    );
  }

  getCoordinatorEmployees(userId: number, categoryId: number, serviceId: number): Observable<InteractorResponse<EmployeeDTO[]>> {
    return this.http.get<InteractorResponse<EmployeeDTO[]>>(
      `${baseUrl}/coordinator-employees?userId=${userId}&categoryId=${categoryId}&serviceId=${serviceId}`
    );
  }

  createOrder(order: OrderModel, files: any[]): Observable<InteractorResponse<OrderModel>> {
    let formData: FormData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    formData.append('jsonRequest', JSON.stringify(order));

    return this.http.post<InteractorResponse<OrderModel>>(`${baseUrl}`, formData);
  }

  saveOrder(order: OrderModel, isChangedOrder: boolean, userId: number, files: any[]): Observable<InteractorResponse<OrderModel>> {
    let formData: FormData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    formData.append('jsonRequest', JSON.stringify(order));
    formData.append(
      'jsonOptions',
      JSON.stringify({
        isChangedOrder: isChangedOrder,
        userId: userId,
      })
    );

    return this.http.put<InteractorResponse<OrderModel>>(`${baseUrl}`, formData);
  }

  getTemplate(categoryId: number, serviceId: number): Observable<InteractorResponse<RequestTemplateDTO>> {
    return this.http.get<InteractorResponse<RequestTemplateDTO>>(`${baseUrl}/template?categoryId=${categoryId}&serviceId=${serviceId}`);
  }

  saveStatus(
    orderId: number,
    mark: number,
    userId: number,
    comment: string,
    files: any[],
    newStatusId: number
  ): Observable<InteractorResponse<OrderModel>> {
    let formData: FormData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    formData.append(
      'jsonRequest',
      JSON.stringify({
        id: orderId,
        mark: mark,
        comment: comment,
        performer: {
          id: userId,
        },
        status: {
          id: newStatusId,
        },
      })
    );

    return this.http.put<InteractorResponse<OrderModel>>(`${baseUrl}/change-status`, formData);
  }

  getDictionary(dictionary: number): Observable<InteractorResponse<T22EntityItemDTO[]>> {
    return this.http.get<InteractorResponse<T22EntityItemDTO[]>>(`${baseUrl}/dictionary?dictionaryId=${dictionary}`);
  }

  getActs(id: number): Observable<InteractorResponse<RequestActDTO[]>> {
    return this.http.get<InteractorResponse<RequestActDTO[]>>(`${baseUrl}/${id}/acts`);
  }

  getAllowedStatuses(id: number, userId: number): Observable<InteractorResponse<StatusDTO[]>> {
    return this.http.get<InteractorResponse<StatusDTO[]>>(`${baseUrl}/${id}/allowed-statuses?userId=${userId}`);
  }

  createAct(id: number, userId: number, value: string): Observable<InteractorResponse<RequestActDTO>> {
    return this.http.post<InteractorResponse<RequestActDTO>>(`${baseUrl}/acts`, {
      requestId: id,
      employee: {
        id: userId,
      },
      description: value,
    });
  }

  canSaveStatus(orderId: number, userId: number, newStatusId: number): Observable<InteractorResponse<boolean>> {
    return this.http.put<InteractorResponse<boolean>>(`${baseUrl}/can-change-status`, {
      id: orderId,
      performer: {
        id: userId,
      },
      status: {
        id: newStatusId,
      },
    });
  }

  getOrdersForNotification(userId: number): Observable<InteractorResponse<OrdersForNotification[]>> {
    return this.http.get<InteractorResponse<OrdersForNotification[]>>(`${baseUrl}/notifications?userId=${userId}`);
  }
}
